import { Panel } from "@/components/ui/Panel";
import { Button } from "@/components/ui/button";
import { FieldLabel } from "@/components/ui/FieldLabel";
import { Note } from "@/components/ui/Note";
import { StatusPill } from "@/components/ui/StatusPill";
import { Textarea } from "@/components/ui/textarea";
import type { IntakeComponentSpec, SlotTag } from "@/lib/intake-components";
import {
  type PendingDescription,
  canAdd,
  descriptionHint,
  tagsFor,
} from "@/lib/supporting-description";

export interface SupportingTagPickerProps {
  spec: IntakeComponentSpec;
  /** The files chosen and not yet added; the first not on its way up is the one asked about. */
  rows: readonly PendingDescription[];
  onChoose: (id: string, tag: SlotTag["id"]) => void;
  onWrite: (id: string, description: string) => void;
  onAdd: (id: string, tag: SlotTag["id"]) => void;
  onCancel: () => void;
}

/**
 * The description as it reads once the file is in: the same words, no longer
 * editable.
 *
 * Rendered through `UploadComponent`'s `renderFileExtra`, so it sits on its own
 * file's row. There is no edit affordance because there is no endpoint behind
 * one: a description is written at the moment the file is accepted, and
 * correcting it means removing the file and adding it again.
 */
export function CommittedDescription({ text }: { text?: string | null }) {
  if (!text) return null;

  return (
    <p className="mt-1.5 border-l-2 border-primary/25 pl-2.5 text-[11.5px] leading-relaxed text-muted-foreground">
      {text}
    </p>
  );
}

/** The words a tag asks for, and the act that adds the file once they are enough. */
function DescribeOther({
  row,
  tag,
  onWrite,
  onAdd,
}: {
  row: PendingDescription;
  tag: SlotTag;
  onWrite: (id: string, description: string) => void;
  onAdd: (id: string, tag: SlotTag["id"]) => void;
}) {
  const fieldId = `describe-${row.id}`;
  const ready = canAdd(tag, row.description);
  return (
    <div className="mt-3 border-t border-primary/20 pt-3">
      <FieldLabel htmlFor={fieldId} look="sentence">
        Describe what this file is{" "}
        <span className="font-medium text-muted-foreground">
          so the tool can make best use of it
        </span>
      </FieldLabel>
      <Textarea
        id={fieldId}
        rows={2}
        value={row.description}
        onChange={(event) => onWrite(row.id, event.target.value)}
        placeholder="e.g. inventor walkthrough of the hilt, recorded after the disclosure"
        className="bg-card text-[12px]"
      />
      <div className="mt-2 flex items-center gap-2.5">
        <Note tone={ready ? "success" : "neutral"} className="flex-1">
          {descriptionHint(tag, row.description)}
        </Note>
        <Button
          type="button"
          size="dense"
          disabled={!ready}
          onClick={() => onAdd(row.id, tag.id)}
        >
          Add File
        </Button>
      </div>
    </div>
  );
}

/**
 * "What is this file?": asked of each supporting file in turn, before it is
 * added. A tag that says enough on its own adds the file at once; one that
 * asks for words opens a field for them.
 *
 * Presentational and controlled. The files and what has been written about
 * them live with the caller, so a failed upload cannot lose a paragraph to a
 * remount.
 */
export function SupportingTagPicker({
  spec,
  rows,
  onChoose,
  onWrite,
  onAdd,
  onCancel,
}: SupportingTagPickerProps) {
  // A file on its way up has been answered for, so the next one is asked
  // about at once; a refused one comes back here with its reason.
  const waiting = rows.filter((r) => r.status !== "sending");
  const row = waiting.at(0);
  if (row === undefined) return null;
  const chosen = spec.tags.find((tag) => tag.id === row.tag);
  const more = waiting.length - 1;

  return (
    <Panel
      tone="warning"
      pad="tight"
      data-testid="supporting-picker"
      className="mt-2.5"
    >
      <div className="mb-2.5 flex flex-wrap items-center gap-2 text-[11.5px] font-bold">
        <span id={`what-${row.id}`}>What is this file?</span>
        <span className="font-mono text-[10.5px] font-medium text-muted-foreground">
          {row.fileName}
        </span>
        {more > 0 && (
          <StatusPill tone="warning" size="sm" className="ml-auto">
            {more} more to tag
          </StatusPill>
        )}
      </div>
      <div
        role="group"
        aria-labelledby={`what-${row.id}`}
        className="flex flex-wrap items-center gap-1.5"
      >
        {tagsFor(spec, row.contentType).map((tag) => (
          <Button
            key={tag.id}
            type="button"
            size="option"
            variant={tag.id === row.tag ? "primary" : "secondary"}
            aria-pressed={tag.id === row.tag}
            onClick={() =>
              tag.descriptionMin === null
                ? onAdd(row.id, tag.id)
                : onChoose(row.id, tag.id)
            }
          >
            {tag.label}
          </Button>
        ))}
        <Button
          type="button"
          size="dense"
          variant="quiet"
          className="ml-auto"
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
      {chosen !== undefined && chosen.descriptionMin !== null && (
        <DescribeOther row={row} tag={chosen} onWrite={onWrite} onAdd={onAdd} />
      )}
      {row.status === "failed" && row.message && (
        <Panel
          as="p"
          shape="row"
          pad="tight"
          tone="destructive"
          role="alert"
          className="mt-2 text-[11px]"
        >
          {row.message}
        </Panel>
      )}
    </Panel>
  );
}
